const BASE_URL = 'https://api.jolpi.ca/ergast/f1';

export const f1Service = {
    async getNextRace() {
        try {
            // Fetch all races for the season to determine the "next" race manually
            const response = await fetch(`${BASE_URL}/current.json`);
            const data = await response.json();
            const races = data.MRData.RaceTable.Races;

            const now = new Date();

            for (const race of races) {
                // Configura la scadenza esattamente alla mezzanotte (23:59:59) del giorno della gara, ora locale
                const endOfRaceDay = new Date(`${race.date}T23:59:59`);

                // Allow the race to be considered "next" until midnight of the race day
                if (now.getTime() <= endOfRaceDay.getTime()) {
                    return race;
                }
            }
            // If all races are fully past, return the last race of the season
            return races[races.length - 1] || null;
        } catch (error) {
            console.error('Error fetching next race:', error);
            return null;
        }
    },

    async getLastRaceResults() {
        try {
            let response = await fetch(`${BASE_URL}/current/last/results.json`);
            let data = await response.json();

            // If the current season just started and has no results, fetch the last race of the previous year
            if (!data.MRData.RaceTable.Races || data.MRData.RaceTable.Races.length === 0) {
                const year = new Date().getFullYear() - 1;
                response = await fetch(`${BASE_URL}/${year}/last/results.json`);
                data = await response.json();
            }

            return data.MRData.RaceTable.Races[0];
        } catch (error) {
            console.error('Error fetching last race results:', error);
            return null;
        }
    },

    async getDriverStandings(year = 'current', limit = 100) {
        try {
            const response = await fetch(`${BASE_URL}/${year}/driverstandings.json?limit=${limit}`);
            const data = await response.json();
            let standings = data.MRData.StandingsTable.StandingsLists[0]?.DriverStandings || [];

            // Fallback to previous year if current data is empty
            if (standings.length === 0 && year === 'current') {
                const prevYear = new Date().getFullYear() - 1;
                const prevResponse = await fetch(`${BASE_URL}/${prevYear}/driverstandings.json?limit=${limit}`);
                const prevData = await prevResponse.json();
                standings = prevData.MRData.StandingsTable.StandingsLists[0]?.DriverStandings || [];
            }

            return standings;
        } catch (error) {
            console.error('Error fetching driver standings:', error);
            return [];
        }
    },

    async getConstructorStandings(year = 'current', limit = 100) {
        try {
            const response = await fetch(`${BASE_URL}/${year}/constructorstandings.json?limit=${limit}`);
            const data = await response.json();
            let standings = data.MRData.StandingsTable.StandingsLists[0]?.ConstructorStandings || [];

            // Fallback to previous year if current data is empty
            if (standings.length === 0 && year === 'current') {
                const prevYear = new Date().getFullYear() - 1;
                const prevResponse = await fetch(`${BASE_URL}/${prevYear}/constructorstandings.json?limit=${limit}`);
                const prevData = await prevResponse.json();
                standings = prevData.MRData.StandingsTable.StandingsLists[0]?.ConstructorStandings || [];
            }

            return standings;
        } catch (error) {
            console.error('Error fetching constructor standings:', error);
            return [];
        }
    },

    async getAllRaces() {
        try {
            const response = await fetch(`${BASE_URL}/current.json`);
            const data = await response.json();
            return data.MRData.RaceTable.Races;
        } catch (error) {
            console.error('Error fetching all races:', error);
            return [];
        }
    },

    async getRaceDetails(season, round) {
        try {
            const response = await fetch(`${BASE_URL}/${season}/${round}.json`);
            const data = await response.json();
            return data.MRData.RaceTable.Races[0];
        } catch (error) {
            console.error(`Error fetching race details for ${season} round ${round}:`, error);
            return null;
        }
    },

    async getCircuitDetails(circuitId) {
        try {
            // Fetch laps from 2025 results (standard reference)
            const response = await fetch(`${BASE_URL}/2025/circuits/${circuitId}/results.json?limit=1`);
            const data = await response.json();
            const race = data.MRData.RaceTable.Races[0];
            const laps = race ? race.Results[0].laps : 'TBA';

            // Circuit lengths (Hardcoded as API doesn't provide them)
            const lengths = {
                'bahrain': '5.412',
                'jeddah': '6.175',
                'albert_park': '5.278',
                'suzuka': '5.807',
                'shanghai': '5.451',
                'miami': '5.412',
                'imola': '4.909',
                'monaco': '3.337',
                'villeneuve': '4.361',
                'catalunya': '4.657',
                'red_bull_ring': '4.318',
                'silverstone': '5.891',
                'hungaroring': '4.381',
                'spa': '7.004',
                'zandvoort': '4.259',
                'monza': '5.793',
                'baku': '6.003',
                'marina_bay': '4.940',
                'americas': '5.513',
                'rodriguez': '4.304',
                'interlagos': '4.309',
                'las_vegas': '6.201',
                'losail': '5.419',
                'yas_marina': '5.281'
            };

            return {
                laps: laps,
                length: lengths[circuitId] || 'TBA'
            };
        } catch (error) {
            console.error('Error fetching circuit details:', error);
            return { laps: 'TBA', length: 'TBA' };
        }
    },

    async getRaceResults(season, round) {
        try {
            const response = await fetch(`${BASE_URL}/${season}/${round}/results.json`);
            const data = await response.json();
            return data.MRData.RaceTable.Races[0]?.Results || [];
        } catch (error) {
            console.error(`Error fetching race results for ${season} round ${round}:`, error);
            return [];
        }
    },

    async getQualifyingResults(season, round) {
        try {
            const response = await fetch(`${BASE_URL}/${season}/${round}/qualifying.json`);
            const data = await response.json();
            return data.MRData.RaceTable.Races[0]?.QualifyingResults || [];
        } catch (error) {
            console.error(`Error fetching qualifying results for ${season} round ${round}:`, error);
            return [];
        }
    },

    async getSprintResults(season, round) {
        try {
            const response = await fetch(`${BASE_URL}/${season}/${round}/sprint.json`);
            const data = await response.json();
            return data.MRData.RaceTable.Races[0]?.SprintResults || [];
        } catch (error) {
            console.error(`Error fetching sprint results for ${season} round ${round}:`, error);
            return [];
        }
    },

    async getPitStops(season, round) {
        try {
            const response = await fetch(`${BASE_URL}/${season}/${round}/pitstops.json?limit=100`);
            const data = await response.json();
            return data.MRData.RaceTable.Races[0]?.PitStops || [];
        } catch (error) {
            console.error(`Error fetching pitstops for ${season} round ${round}:`, error);
            return [];
        }
    },

    async getStatus(season) {
        try {
            const response = await fetch(`${BASE_URL}/${season}/status.json?limit=150`);
            const data = await response.json();
            return data.MRData.StatusTable.Status || [];
        } catch (error) {
            console.error(`Error fetching status for ${season}:`, error);
            return [];
        }
    },

    async getAllDrivers(year = 'current') {
        try {
            const response = await fetch(`${BASE_URL}/${year}/drivers.json?limit=100`);
            const data = await response.json();
            return data.MRData.DriverTable.Drivers || [];
        } catch (error) {
            console.error(`Error fetching drivers for ${year}:`, error);
            return [];
        }
    },

    async getConstructorForDriver(year, driverId) {
        try {
            const response = await fetch(`${BASE_URL}/${year}/drivers/${driverId}/constructors.json`);
            const data = await response.json();
            return data.MRData.ConstructorTable.Constructors[0] || null;
        } catch (error) {
            console.error(`Error fetching constructor for ${driverId} in ${year}:`, error);
            return null;
        }
    },

    async getDriverStandingsForDriver(driverId, year = 'current') {
        try {
            const response = await fetch(`${BASE_URL}/${year}/drivers/${driverId}/driverstandings.json`);
            const data = await response.json();
            let standing = data.MRData.StandingsTable.StandingsLists[0]?.DriverStandings[0] || null;

            // Fallback to previous year if current data is empty
            if (!standing && year === 'current') {
                const prevYear = new Date().getFullYear() - 1;
                const prevResponse = await fetch(`${BASE_URL}/${prevYear}/drivers/${driverId}/driverstandings.json`);
                const prevData = await prevResponse.json();
                standing = prevData.MRData.StandingsTable.StandingsLists[0]?.DriverStandings[0] || null;
            }

            return standing;
        } catch (error) {
            console.error(`Error fetching standings for driver ${driverId} in ${year}:`, error);
            return null;
        }
    },

    async getDriverResults(driverId, year = 'current', limit = 100) {
        try {
            const url = year === 'all'
                ? `${BASE_URL}/drivers/${driverId}/results.json?limit=${limit}`
                : `${BASE_URL}/${year}/drivers/${driverId}/results.json?limit=${limit}`;
            const response = await fetch(url);
            const data = await response.json();
            let results = data.MRData.RaceTable.Races || [];

            // Fallback to previous year if current data is empty (e.g. before season starts)
            if (results.length === 0 && year === 'current') {
                const prevYear = new Date().getFullYear() - 1;
                const prevResponse = await fetch(`${BASE_URL}/${prevYear}/drivers/${driverId}/results.json?limit=${limit}`);
                const prevData = await prevResponse.json();
                results = prevData.MRData.RaceTable.Races || [];
            }

            return results;
        } catch (error) {
            console.error(`Error fetching results for driver ${driverId} in ${year}:`, error);
            return [];
        }
    },

    async getConstructorStandingsForTeam(constructorId, year = 'current') {
        try {
            const response = await fetch(`${BASE_URL}/${year}/constructors/${constructorId}/constructorstandings.json`);
            const data = await response.json();
            let standing = data.MRData.StandingsTable.StandingsLists[0]?.ConstructorStandings[0] || null;

            // Fallback to previous year if current data is empty
            if (!standing && year === 'current') {
                const prevYear = new Date().getFullYear() - 1;
                const prevResponse = await fetch(`${BASE_URL}/${prevYear}/constructors/${constructorId}/constructorstandings.json`);
                const prevData = await prevResponse.json();
                standing = prevData.MRData.StandingsTable.StandingsLists[0]?.ConstructorStandings[0] || null;
            }

            return standing;
        } catch (error) {
            console.error(`Error fetching standings for team ${constructorId}:`, error);
            return null;
        }
    },

    async getConstructorResults(constructorId, year = 'current', limit = 10) {
        try {
            const response = await fetch(`${BASE_URL}/${year}/constructors/${constructorId}/results.json?limit=${limit}`);
            const data = await response.json();
            let results = data.MRData.RaceTable.Races || [];

            // Fallback to previous year if current data is empty (e.g. before season starts)
            if (results.length === 0 && year === 'current') {
                const prevYear = new Date().getFullYear() - 1;
                const prevResponse = await fetch(`${BASE_URL}/${prevYear}/constructors/${constructorId}/results.json?limit=${limit}`);
                const prevData = await prevResponse.json();
                results = prevData.MRData.RaceTable.Races || [];
            }

            return results;
        } catch (error) {
            console.error(`Error fetching results for team ${constructorId}:`, error);
            return [];
        }
    },

    async getAllWinners(year) {
        try {
            const response = await fetch(`${BASE_URL}/${year}/results/1.json?limit=100`);
            const data = await response.json();
            return data.MRData.RaceTable.Races || [];
        } catch (error) {
            console.error(`Error fetching all winners for ${year}:`, error);
            return [];
        }
    },

    async getAllPoles(year) {
        try {
            const response = await fetch(`${BASE_URL}/${year}/qualifying/1.json?limit=100`);
            const data = await response.json();
            return data.MRData.RaceTable.Races || [];
        } catch (error) {
            console.error(`Error fetching all poles for ${year}:`, error);
            return [];
        }
    },

    async getAllFastestLaps(year) {
        try {
            const response = await fetch(`${BASE_URL}/${year}/fastest/1/results.json?limit=100`);
            const data = await response.json();
            return data.MRData.RaceTable.Races || [];
        } catch (error) {
            console.error(`Error fetching all fastest laps for ${year}:`, error);
            return [];
        }
    },

    async getAllRaceResults(year) {
        try {
            // Fetch all races in the season with their results (max 1000 items to cover all drivers in all races)
            const response = await fetch(`${BASE_URL}/${year}/results.json?limit=1000`);
            const data = await response.json();
            return data.MRData.RaceTable.Races || [];
        } catch (error) {
            console.error(`Error fetching all race results for ${year}:`, error);
            return [];
        }
    }
};
